import { ROUTES } from '@shared/utils';
import type { SportsbookRouteCategory } from '../SportsbookRoutesContext/SportsbookRoutesContext';
import type { RouteParams } from '../SportsbookWidget/types';

const ROOT = ROUTES.SPORTS.ROOT_SPORTSBOOK_PATHNAME;

/**
 * Resolves a target Next.js pathname from Altenar's routeParams and the app's route config.
 * Only mapped sports and championships get a pretty path; every other route (unmapped sports and championships,
 * countries, racing, events, hub pages…) resolves to the /sportsbook hub, where the caller keeps it as query params.
 * Returns null when there is nothing to navigate to (no route config or no route params).
 */
function resolveSportsbookPathname(routeParams: RouteParams, categories: SportsbookRouteCategory[]): string | null {
  // Without route config the widget must keep its original query-param-only behaviour.
  if (categories.length === 0 || Object.keys(routeParams).length === 0) return null;

  // Rule 1: championship page → competition page. Other routes may also carry championshipIds (events, racing…) but are not mapped.
  const championshipIds = routeParams.championshipIds ?? (routeParams.champId !== undefined ? [routeParams.champId] : undefined);

  if (routeParams.page === 'championship' && championshipIds?.length) {
    for (const category of categories) {
      for (const competition of category.competitions) {
        if (championshipIds.some(id => competition.championshipIds.includes(id))) {
          return `${ROOT}/${category.slug}/${competition.slug}`;
        }
      }
    }
  }

  // Rule 2: page=sport + sportId → category page
  if (routeParams.page === 'sport' && routeParams.sportId !== undefined) {
    const category = categories.find(cat => cat.sportId === routeParams.sportId);
    if (category) return `${ROOT}/${category.slug}`;
  }

  // Rule 3: everything else is unmapped → hub, driven by query params
  return ROOT;
}

export { resolveSportsbookPathname };
